import React from "react";
import { Warning as WarningIcon } from "@mui/icons-material";
import Box from "@mui/material/Box";
import Popover from "@mui/material/Popover";

export interface ConflictNoficationBadgeProps {
  id: string;
  children: React.ReactNode;
  disabled?: boolean;
}

const ConflictNotificationBadge: React.FC<ConflictNoficationBadgeProps> = ({
  id,
  children,
  disabled,
}) => {
  const anchorRef = React.useRef<HTMLElement | null>(null);
  const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const [open, setOpen] = React.useState<boolean>(false);

  const cancelTimer = React.useCallback(() => {
    if (!!timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  function handleMouseEnter(event: React.MouseEvent<HTMLElement>) {
    cancelTimer();
    setOpen(true);
  }

  function handleMouseLeave() {
    cancelTimer();
    timerRef.current = setTimeout(() => setOpen(false), 150);
  }

  React.useEffect(() => cancelTimer(), [cancelTimer]);

  return (
    <React.Fragment>
      <Box
        aria-owns={open ? `${id}-notif-badge-popover` : undefined}
        aria-haspopup="true"
        display="flex"
        ref={anchorRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <WarningIcon color="warning" />
      </Box>
      <Popover
        id={`${id}-notif-badge-popover`}
        sx={{ pointerEvents: "none" }}
        open={open}
        anchorEl={anchorRef.current}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        onClose={() => setOpen(false)}
        slotProps={{
          paper: {
            onMouseEnter: handleMouseEnter,
            onMouseLeave: handleMouseLeave,
            sx: {
              pointerEvents: "auto",
              p: 1,
              maxWidth: "200px",
              maxHeight: "400px",
              overflow: "auto",
            },
          },
        }}
      >
        {children}
      </Popover>
    </React.Fragment>
  );
};

export default ConflictNotificationBadge;
